import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ContactModal from './ContactModal';
import Footer from './Footer';

const LandingPage = ({ theme = 'dark' }) => {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [contactModalVisible, setContactModalVisible] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleGetStarted = () => {
    navigate('/register');
  };

  const handleWatchDemo = () => {
    window.open('https://demo.recapifyapp.com', '_blank');
  };

  const handleContactSales = () => {
    setContactModalVisible(true);
  };

  const isDark = theme === 'dark';

  // Shared card styles for cards on GRADIENT backgrounds (Features section)
  const sharedGradientCardStyles = {
    background: isDark ? 'linear-gradient(135deg, #1e1e3f 0%, #2a2a4a 100%)' : 'linear-gradient(135deg, #ffffff 0%, #f9fafb 100%)',
    padding: '40px',
    borderRadius: '16px',
    boxShadow: isDark ? '0 8px 25px rgba(102, 126, 234, 0.25)' : '0 12px 35px rgba(0,0,0,0.15)',
    border: isDark ? '1px solid rgba(102, 126, 234, 0.4)' : '1px solid rgba(59, 130, 246, 0.2)',
    transform: 'translateY(-8px)',
    textAlign: 'center',
  };

  // Shared card styles for cards on REGULAR backgrounds (Flow, Security sections)
  const sharedRegularCardStyles = {
    background: isDark ? 'rgba(26, 26, 46, 0.6)' : 'rgba(255, 255, 255, 0.8)',
    padding: '40px',
    borderRadius: '16px',
    boxShadow: isDark ? '0 12px 35px rgba(102, 126, 234, 0.5)' : '0 12px 35px rgba(0,0,0,0.15)',
    border: isDark ? '1px solid rgba(102, 126, 234, 0.3)' : '1px solid rgba(0, 0, 0, 0.08)',
    transform: 'translateY(-8px)',
    textAlign: 'center',
  };

  const sharedCardTitleStyles = {
    fontSize: '1.5rem',
    fontWeight: '700',
    marginBottom: '15px',
    color: isDark ? '#ffffff' : '#1f2937',
  };

  const sharedCardDescriptionStyles = {
    fontSize: '1rem',
    color: isDark ? '#b0b0b0' : '#6b7280',
    lineHeight: '1.6',
  };

  const sharedCardIconStyles = {
    fontSize: '3rem',
    marginBottom: '20px',
  };

  const sharedGridStyles = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '30px',
    marginTop: '40px',
  };

  const styles = {
    container: {
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      color: isDark ? '#e0e0e0' : '#1f2937',
      lineHeight: '1.6',
      background: isDark ? '#0f0f1e' : '#f9fafb',
    },
    hero: {
      background: isDark ? `
        radial-gradient(ellipse 1200px 800px at 50% 50%, rgba(22, 33, 62, 0.9) 0%, transparent 60%),
        radial-gradient(ellipse 600px 400px at 30% 30%, rgba(15, 52, 96, 0.7) 0%, transparent 50%),
        radial-gradient(ellipse 700px 500px at 70% 60%, rgba(26, 26, 46, 0.8) 0%, transparent 50%),
        radial-gradient(ellipse 500px 600px at 20% 80%, rgba(22, 33, 62, 0.6) 0%, transparent 50%),
        radial-gradient(ellipse 800px 400px at 80% 20%, rgba(15, 52, 96, 0.5) 0%, transparent 50%),
        radial-gradient(ellipse 900px 700px at 50% 40%, rgba(26, 26, 46, 0.7) 0%, transparent 55%),
        radial-gradient(ellipse 400px 300px at 10% 10%, rgba(22, 33, 62, 0.4) 0%, transparent 50%),
        radial-gradient(ellipse 500px 400px at 90% 90%, rgba(15, 52, 96, 0.4) 0%, transparent 50%),
        radial-gradient(ellipse 450px 350px at 15% 45%, rgba(26, 26, 46, 0.5) 0%, transparent 50%),
        radial-gradient(ellipse 550px 450px at 85% 55%, rgba(22, 33, 62, 0.45) 0%, transparent 50%),
        radial-gradient(ellipse 350px 500px at 5% 70%, rgba(15, 52, 96, 0.4) 0%, transparent 50%),
        radial-gradient(ellipse 600px 350px at 95% 30%, rgba(26, 26, 46, 0.45) 0%, transparent 50%),
        radial-gradient(ellipse 400px 400px at 40% 15%, rgba(22, 33, 62, 0.4) 0%, transparent 50%),
        radial-gradient(ellipse 500px 350px at 60% 85%, rgba(15, 52, 96, 0.4) 0%, transparent 50%),
        radial-gradient(ellipse 300px 400px at 25% 60%, rgba(26, 26, 46, 0.35) 0%, transparent 50%),
        radial-gradient(ellipse 450px 300px at 75% 40%, rgba(22, 33, 62, 0.35) 0%, transparent 50%),
        radial-gradient(ellipse 350px 300px at 12% 25%, rgba(15, 52, 96, 0.3) 0%, transparent 50%),
        radial-gradient(ellipse 400px 350px at 88% 75%, rgba(26, 26, 46, 0.3) 0%, transparent 50%),
        radial-gradient(ellipse 320px 280px at 8% 50%, rgba(22, 33, 62, 0.25) 0%, transparent 50%),
        radial-gradient(ellipse 380px 320px at 92% 50%, rgba(15, 52, 96, 0.25) 0%, transparent 50%),
        radial-gradient(ellipse 280px 350px at 18% 18%, rgba(26, 26, 46, 0.25) 0%, transparent 50%),
        radial-gradient(ellipse 340px 300px at 82% 82%, rgba(22, 33, 62, 0.25) 0%, transparent 50%),
        radial-gradient(ellipse 300px 250px at 35% 8%, rgba(15, 52, 96, 0.2) 0%, transparent 50%),
        radial-gradient(ellipse 320px 280px at 65% 92%, rgba(26, 26, 46, 0.2) 0%, transparent 50%),
        radial-gradient(ellipse 250px 300px at 3% 35%, rgba(22, 33, 62, 0.2) 0%, transparent 50%),
        radial-gradient(ellipse 280px 260px at 97% 65%, rgba(15, 52, 96, 0.2) 0%, transparent 50%),
        radial-gradient(ellipse 260px 240px at 22% 72%, rgba(26, 26, 46, 0.18) 0%, transparent 50%),
        radial-gradient(ellipse 290px 250px at 78% 28%, rgba(22, 33, 62, 0.18) 0%, transparent 50%),
        radial-gradient(ellipse 240px 280px at 45% 5%, rgba(15, 52, 96, 0.15) 0%, transparent 50%),
        radial-gradient(ellipse 270px 240px at 55% 95%, rgba(26, 26, 46, 0.15) 0%, transparent 50%),
        radial-gradient(ellipse 220px 260px at 6% 88%, rgba(22, 33, 62, 0.15) 0%, transparent 50%),
        radial-gradient(ellipse 250px 230px at 94% 12%, rgba(15, 52, 96, 0.15) 0%, transparent 50%),
        radial-gradient(ellipse 230px 220px at 32% 38%, rgba(26, 26, 46, 0.12) 0%, transparent 50%),
        radial-gradient(ellipse 240px 210px at 68% 62%, rgba(22, 33, 62, 0.12) 0%, transparent 50%),
        radial-gradient(ellipse 210px 240px at 14% 55%, rgba(15, 52, 96, 0.12) 0%, transparent 50%),
        radial-gradient(ellipse 230px 200px at 86% 45%, rgba(26, 26, 46, 0.12) 0%, transparent 50%),
        radial-gradient(ellipse 200px 220px at 28% 82%, rgba(22, 33, 62, 0.1) 0%, transparent 50%),
        radial-gradient(ellipse 220px 190px at 72% 18%, rgba(15, 52, 96, 0.1) 0%, transparent 50%),
        linear-gradient(135deg, #0f0f1e 0%, #1a1a2e 25%, #16213e 50%, #0f3460 75%, #1a1a2e 100%)
      ` : `
        radial-gradient(ellipse 1200px 800px at 50% 50%, rgba(219, 234, 254, 0.9) 0%, transparent 60%),
        radial-gradient(ellipse 600px 400px at 30% 30%, rgba(224, 231, 255, 0.7) 0%, transparent 50%),
        radial-gradient(ellipse 700px 500px at 70% 60%, rgba(243, 244, 246, 0.8) 0%, transparent 50%),
        radial-gradient(ellipse 500px 600px at 20% 80%, rgba(219, 234, 254, 0.6) 0%, transparent 50%),
        radial-gradient(ellipse 800px 400px at 80% 20%, rgba(224, 231, 255, 0.5) 0%, transparent 50%),
        radial-gradient(ellipse 900px 700px at 50% 40%, rgba(243, 244, 246, 0.7) 0%, transparent 55%),
        radial-gradient(ellipse 400px 300px at 10% 10%, rgba(219, 234, 254, 0.4) 0%, transparent 50%),
        radial-gradient(ellipse 500px 400px at 90% 90%, rgba(224, 231, 255, 0.4) 0%, transparent 50%),
        radial-gradient(ellipse 450px 350px at 15% 45%, rgba(243, 244, 246, 0.5) 0%, transparent 50%),
        radial-gradient(ellipse 550px 450px at 85% 55%, rgba(219, 234, 254, 0.45) 0%, transparent 50%),
        radial-gradient(ellipse 350px 500px at 5% 70%, rgba(224, 231, 255, 0.4) 0%, transparent 50%),
        radial-gradient(ellipse 600px 350px at 95% 30%, rgba(243, 244, 246, 0.45) 0%, transparent 50%),
        radial-gradient(ellipse 400px 400px at 40% 15%, rgba(219, 234, 254, 0.4) 0%, transparent 50%),
        radial-gradient(ellipse 500px 350px at 60% 85%, rgba(224, 231, 255, 0.4) 0%, transparent 50%),
        radial-gradient(ellipse 300px 400px at 25% 60%, rgba(243, 244, 246, 0.35) 0%, transparent 50%),
        radial-gradient(ellipse 450px 300px at 75% 40%, rgba(219, 234, 254, 0.35) 0%, transparent 50%),
        radial-gradient(ellipse 350px 300px at 12% 25%, rgba(224, 231, 255, 0.3) 0%, transparent 50%),
        radial-gradient(ellipse 400px 350px at 88% 75%, rgba(243, 244, 246, 0.3) 0%, transparent 50%),
        radial-gradient(ellipse 320px 280px at 8% 50%, rgba(219, 234, 254, 0.25) 0%, transparent 50%),
        radial-gradient(ellipse 380px 320px at 92% 50%, rgba(224, 231, 255, 0.25) 0%, transparent 50%),
        radial-gradient(ellipse 280px 350px at 18% 18%, rgba(243, 244, 246, 0.25) 0%, transparent 50%),
        radial-gradient(ellipse 340px 300px at 82% 82%, rgba(219, 234, 254, 0.25) 0%, transparent 50%),
        radial-gradient(ellipse 300px 250px at 35% 8%, rgba(224, 231, 255, 0.2) 0%, transparent 50%),
        radial-gradient(ellipse 320px 280px at 65% 92%, rgba(243, 244, 246, 0.2) 0%, transparent 50%),
        radial-gradient(ellipse 250px 300px at 3% 35%, rgba(219, 234, 254, 0.2) 0%, transparent 50%),
        radial-gradient(ellipse 280px 260px at 97% 65%, rgba(224, 231, 255, 0.2) 0%, transparent 50%),
        radial-gradient(ellipse 260px 240px at 22% 72%, rgba(243, 244, 246, 0.18) 0%, transparent 50%),
        radial-gradient(ellipse 290px 250px at 78% 28%, rgba(219, 234, 254, 0.18) 0%, transparent 50%),
        radial-gradient(ellipse 240px 280px at 45% 5%, rgba(224, 231, 255, 0.15) 0%, transparent 50%),
        radial-gradient(ellipse 270px 240px at 55% 95%, rgba(243, 244, 246, 0.15) 0%, transparent 50%),
        radial-gradient(ellipse 220px 260px at 6% 88%, rgba(219, 234, 254, 0.15) 0%, transparent 50%),
        radial-gradient(ellipse 250px 230px at 94% 12%, rgba(224, 231, 255, 0.15) 0%, transparent 50%),
        radial-gradient(ellipse 230px 220px at 32% 38%, rgba(243, 244, 246, 0.12) 0%, transparent 50%),
        radial-gradient(ellipse 240px 210px at 68% 62%, rgba(219, 234, 254, 0.12) 0%, transparent 50%),
        radial-gradient(ellipse 210px 240px at 14% 55%, rgba(224, 231, 255, 0.12) 0%, transparent 50%),
        radial-gradient(ellipse 230px 200px at 86% 45%, rgba(243, 244, 246, 0.12) 0%, transparent 50%),
        radial-gradient(ellipse 200px 220px at 28% 82%, rgba(219, 234, 254, 0.1) 0%, transparent 50%),
        radial-gradient(ellipse 220px 190px at 72% 18%, rgba(224, 231, 255, 0.1) 0%, transparent 50%),
        linear-gradient(135deg, #eff6ff 0%, #f9fafb 25%, #dbeafe 50%, #e0e7ff 75%, #f9fafb 100%)
      `,
      color: isDark ? 'white' : '#1f2937',
      padding: '100px 20px',
      textAlign: 'center',
      position: 'relative',
      overflow: 'hidden',
    },
    heroTitle: {
      fontSize: 'clamp(2rem, 5vw, 3.5rem)',
      fontWeight: '800',
      marginBottom: '20px',
      lineHeight: '1.2',
      textShadow: '0 2px 4px rgba(0,0,0,0.1)',
    },
    heroSubtitle: {
      fontSize: 'clamp(1rem, 2.5vw, 1.5rem)',
      marginBottom: '30px',
      opacity: '0.95',
      maxWidth: '800px',
      margin: '0 auto 30px',
    },
    heroDescription: {
      fontSize: 'clamp(0.9rem, 2vw, 1.1rem)',
      marginBottom: '40px',
      opacity: '0.9',
      maxWidth: '900px',
      margin: '0 auto 40px',
      lineHeight: '1.8',
    },
    buttonContainer: {
      display: 'flex',
      gap: '15px',
      justifyContent: 'center',
      flexWrap: 'wrap',
      marginBottom: '30px',
    },
    buttonPrimary: {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      padding: '16px 32px',
      fontSize: '1.1rem',
      fontWeight: '600',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      boxShadow: '0 4px 20px rgba(102, 126, 234, 0.4)',
      transition: 'transform 0.2s, box-shadow 0.2s',
    },
    buttonSecondary: {
      background: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
      color: isDark ? 'white' : '#1f2937',
      padding: '16px 32px',
      fontSize: '1.1rem',
      fontWeight: '600',
      border: isDark ? '2px solid rgba(255,255,255,0.3)' : '2px solid rgba(0,0,0,0.2)',
      borderRadius: '8px',
      cursor: 'pointer',
      transition: 'all 0.2s',
      backdropFilter: 'blur(10px)',
    },
    section: {
      padding: '80px 20px',
      maxWidth: '1200px',
      margin: '0 auto',
    },
    sectionAlt: {
      padding: '80px 20px',
      background: isDark ? 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)' : 'linear-gradient(135deg, #dbeafe 0%, #e0e7ff 50%, #dbeafe 100%)',
    },
    sectionAltInner: {
      maxWidth: '1200px',
      margin: '0 auto',
    },
    sectionTitle: {
      fontSize: 'clamp(1.8rem, 4vw, 2.5rem)',
      fontWeight: '700',
      textAlign: 'center',
      marginBottom: '20px',
      color: isDark ? '#ffffff' : '#1f2937',
    },
    sectionSubtitle: {
      fontSize: 'clamp(1rem, 2vw, 1.2rem)',
      textAlign: 'center',
      marginBottom: '60px',
      color: isDark ? '#d0d0d0' : '#4b5563',
      maxWidth: '700px',
      margin: '0 auto 60px',
    },
    platformsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
      gap: '20px',
      marginTop: '40px',
    },
    platformCard: {
      background: isDark ? 'linear-gradient(135deg, #1e1e3f 0%, #2a2a4a 100%)' : 'linear-gradient(135deg, #ffffff 0%, #f3f4f6 100%)',
      padding: '25px 15px',
      borderRadius: '12px',
      textAlign: 'center',
      fontWeight: '600',
      fontSize: '0.95rem',
      cursor: 'default',
      boxShadow: isDark ? '0 8px 25px rgba(102, 126, 234, 0.25)' : '0 12px 35px rgba(0,0,0,0.12)',
      border: isDark ? '1px solid rgba(102, 126, 234, 0.4)' : '1px solid rgba(59, 130, 246, 0.2)',
      color: isDark ? '#e0e0e0' : '#1f2937',
      transform: 'translateY(-8px)',
    },
    featuresGrid: sharedGridStyles,
    featureCard: sharedGradientCardStyles,
    featureIcon: sharedCardIconStyles,
    featureTitle: sharedCardTitleStyles,
    featureDescription: sharedCardDescriptionStyles,
    badge: {
      display: 'inline-block',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      padding: '6px 12px',
      borderRadius: '20px',
      fontSize: '0.85rem',
      fontWeight: '600',
    },
    badgeGreen: {
      display: 'inline-block',
      background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
      color: 'white',
      padding: '6px 12px',
      borderRadius: '20px',
      fontSize: '0.85rem',
      fontWeight: '600',
    },
    stepsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: '40px',
      marginTop: '40px',
    },
    stepCard: sharedRegularCardStyles,
    stepNumber: {
      fontSize: '4rem',
      fontWeight: '800',
      color: '#ffffff',
      marginBottom: '20px',
    },
    stepTitle: {
      fontSize: '1.5rem',
      fontWeight: '700',
      marginBottom: '15px',
      color: isDark ? '#ffffff' : '#1f2937',
    },
    stepDescription: {
      fontSize: '1rem',
      color: isDark ? '#b0b0b0' : '#6b7280',
      lineHeight: '1.6',
    },
    pricingGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: '30px',
      marginTop: '40px',
      maxWidth: '900px',
      margin: '40px auto 0',
      padding: '0 20px',
    },
    pricingCard: {
      background: isDark ? 'rgba(26, 26, 46, 0.6)' : 'rgba(255, 255, 255, 0.8)',
      padding: '40px',
      borderRadius: '16px',
      boxShadow: isDark ? '0 12px 35px rgba(102, 126, 234, 0.5)' : '0 12px 35px rgba(0,0,0,0.15)',
      border: isDark ? '1px solid rgba(102, 126, 234, 0.3)' : '1px solid rgba(0, 0, 0, 0.08)',
      color: isDark ? '#e0e0e0' : '#1f2937',
      transform: 'translateY(-8px)',
    },
    pricingCardFeatured: {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      transform: 'scale(1.05)',
      border: '2px solid rgba(255, 255, 255, 0.2)',
      boxShadow: '0 12px 40px rgba(102, 126, 234, 0.6)',
    },
    pricingTitle: {
      fontSize: '2.2rem',
      fontWeight: '700',
      marginBottom: '15px',
    },
    pricingDescription: {
      fontSize: '1rem',
      marginBottom: '30px',
      opacity: '0.9',
      lineHeight: '1.5',
    },
    price: {
      fontSize: '2rem',
      fontWeight: '800',
      marginBottom: '8px',
      textAlign: 'center',
    },
    priceUnit: {
      fontSize: '0.95rem',
      opacity: '0.7',
      marginBottom: '30px',
      textAlign: 'center',
    },
    featureList: {
      textAlign: 'left',
      marginBottom: '30px',
      display: 'inline-block',
      width: 'auto',
    },
    featureItem: {
      marginBottom: '12px',
      fontSize: '0.95rem',
    },
    ctaSection: {
      background: isDark ? 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)' : 'linear-gradient(135deg, #dbeafe 0%, #e0e7ff 50%, #dbeafe 100%)',
      color: isDark ? 'white' : '#1f2937',
      padding: '100px 20px',
      textAlign: 'center',
      position: 'relative',
      borderTop: isDark ? '2px solid rgba(102, 126, 234, 0.3)' : '2px solid rgba(59, 130, 246, 0.2)',
    },
    ctaTitle: {
      fontSize: 'clamp(1.8rem, 4vw, 2.5rem)',
      fontWeight: '700',
      marginBottom: '20px',
    },
    ctaSubtitle: {
      fontSize: 'clamp(1rem, 2vw, 1.2rem)',
      marginBottom: '40px',
      opacity: '0.95',
    },
    securityGrid: sharedGridStyles,
    securityCard: sharedRegularCardStyles,
    securityIcon: sharedCardIconStyles,
    securityTitle: sharedCardTitleStyles,
    securityDescription: sharedCardDescriptionStyles,
    highlight: {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '15px 25px',
      borderRadius: '8px',
      display: 'inline-block',
      marginBottom: '20px',
      fontWeight: '600',
      color: '#ffffff',
      boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
    },
    screenshotsGrid: {
      display: 'grid',
      gridTemplateColumns: '320px 1fr',
      gap: '60px',
      marginTop: '40px',
      alignItems: 'start',
    },
    screenshotCard: {
      textAlign: 'center',
    },
    screenshotLabel: {
      fontSize: '1.2rem',
      fontWeight: '700',
      marginBottom: '20px',
      color: isDark ? '#ffffff' : '#1f2937',
    },
    screenshotPlaceholder: {
      background: isDark ? 'rgba(26, 26, 46, 0.4)' : 'rgba(255, 255, 255, 0.8)',
      borderRadius: '16px',
      padding: '30px',
      boxShadow: isDark ? '0 8px 30px rgba(0,0,0,0.3)' : '0 8px 30px rgba(0,0,0,0.08)',
      border: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.08)',
      transition: 'transform 0.3s, box-shadow 0.3s',
      minHeight: '400px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    screenshotDescription: {
      marginTop: '20px',
      fontSize: '1rem',
      color: isDark ? '#b0b0b0' : '#6b7280',
    },
    extensionMockup: {
      background: isDark ? 'rgba(26, 26, 46, 0.6)' : '#ffffff',
      borderRadius: '12px',
      width: '320px',
      boxShadow: isDark ? '0 8px 30px rgba(0,0,0,0.4)' : '0 8px 30px rgba(0,0,0,0.15)',
      overflow: 'hidden',
      border: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)',
    },
    extensionHeader: {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '15px',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
    },
    extensionLogo: {
      fontSize: '1.5rem',
    },
    extensionTitle: {
      fontSize: '1.2rem',
      fontWeight: '700',
      color: '#ffffff',
    },
    extensionBody: {
      padding: '25px',
    },
    recordButton: {
      background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
      color: 'white',
      padding: '20px',
      borderRadius: '12px',
      marginBottom: '25px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '10px',
      fontWeight: '700',
      fontSize: '1.1rem',
      boxShadow: '0 4px 15px rgba(239, 68, 68, 0.4)',
    },
    recordIcon: {
      fontSize: '1.5rem',
    },
    recordText: {
      fontSize: '1.1rem',
    },
    extensionOptions: {
      display: 'flex',
      flexDirection: 'column',
      gap: '15px',
      marginBottom: '20px',
    },
    optionRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '12px',
      background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
      borderRadius: '8px',
      fontSize: '0.95rem',
    },
    toggle: {
      width: '40px',
      height: '22px',
      background: isDark ? '#4b5563' : '#d1d5db',
      borderRadius: '11px',
      position: 'relative',
    },
    toggleActive: {
      width: '40px',
      height: '22px',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      borderRadius: '11px',
      position: 'relative',
    },
    extensionStatus: {
      textAlign: 'center',
      fontSize: '0.9rem',
      color: isDark ? '#9ca3af' : '#6b7280',
      fontStyle: 'italic',
    },
    webAppMockup: {
      background: isDark ? 'rgba(26, 26, 46, 0.6)' : '#ffffff',
      borderRadius: '12px',
      width: '100%',
      maxWidth: '500px',
      boxShadow: isDark ? '0 8px 30px rgba(0,0,0,0.4)' : '0 8px 30px rgba(0,0,0,0.15)',
      overflow: 'hidden',
      border: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)',
    },
    webAppHeader: {
      background: isDark ? '#16213e' : '#f3f4f6',
      padding: '15px',
      borderBottom: isDark ? '1px solid rgba(102, 126, 234, 0.2)' : '1px solid rgba(0,0,0,0.1)',
    },
    webAppNav: {
      display: 'flex',
      gap: '15px',
      fontSize: '0.9rem',
    },
    navItem: {
      padding: '8px 12px',
      background: isDark ? 'rgba(102, 126, 234, 0.2)' : 'rgba(59, 130, 246, 0.1)',
      borderRadius: '6px',
      color: isDark ? '#e0e0e0' : '#1f2937',
    },
    webAppBody: {
      padding: '25px',
    },
    recordingCard: {
      display: 'flex',
      alignItems: 'center',
      gap: '15px',
      padding: '15px',
      background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
      borderRadius: '10px',
      marginBottom: '15px',
      border: isDark ? '1px solid rgba(102, 126, 234, 0.2)' : '1px solid rgba(0,0,0,0.1)',
    },
    recordingThumb: {
      fontSize: '2rem',
      width: '50px',
      height: '50px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: isDark ? 'rgba(102, 126, 234, 0.2)' : 'rgba(59, 130, 246, 0.1)',
      borderRadius: '8px',
    },
    recordingInfo: {
      flex: 1,
      textAlign: 'left',
    },
    recordingTitle: {
      fontSize: '1rem',
      fontWeight: '600',
      marginBottom: '5px',
      color: isDark ? '#ffffff' : '#1f2937',
    },
    recordingMeta: {
      fontSize: '0.85rem',
      color: isDark ? '#9ca3af' : '#6b7280',
    },
    searchBar: {
      marginTop: '25px',
      padding: '15px',
      background: isDark ? 'rgba(102, 126, 234, 0.1)' : 'rgba(59, 130, 246, 0.05)',
      borderRadius: '10px',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      border: isDark ? '2px solid rgba(102, 126, 234, 0.3)' : '2px solid rgba(59, 130, 246, 0.2)',
    },
    searchIcon: {
      fontSize: '1.2rem',
    },
    searchText: {
      fontSize: '0.95rem',
      color: isDark ? '#9ca3af' : '#6b7280',
      fontStyle: 'italic',
    },
  };

  const platforms = [
    "Microsoft Teams",
    "Zoom",
    "Google Meet",
    "Webex",
    "Slack Huddles",
    "Discord",
    "YouTube",
    "Podcasts",
    "Any Browser Tab"
  ];

  const features = [
    {
      icon: "🎬",
      title: "One-Click Recording",
      description: "Record any browser media instantly. Capture meetings, videos, podcasts, or your screen with one click. Works with Teams, Zoom, Meet, YouTube, and more."
    },
    {
      icon: "🤖",
      title: "AI Assistant",
      description: "Ask questions and get answers from your entire knowledge base. AI scans all recordings, generates custom responses, and provides citations."
    },
    {
      icon: "🧠",
      title: "AI Summaries",
      description: "Automatically generated summaries from your recordings. Customize prompts to match your structure and priorities. Start with templates, make it yours."
    },
    {
      icon: "📝",
      title: "Automatic Transcription",
      description: "Every recording is transcribed automatically. Choose your transcription method to balance speed vs cost. Searchable, accurate text - ready when you need it."
    },
    {
      icon: "💰",
      title: "Transparent Pricing",
      description: "Pay only for what you use. No subscriptions, no hidden fees. Control costs via settings like transcription method. Monthly invoicing for previous month's consumption."
    },
    {
      icon: "🔐",
      title: "Enterprise Security",
      description: "Bank-level encryption protects your data. Built on AWS infrastructure with Fortune 500-grade security. Your recordings stay private - never shared, never used to train AI models."
    }
  ];

  return (
    <div style={styles.container}>
      {/* Hero Section */}
      <div style={styles.hero}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h1 style={styles.heroTitle}>
            Never Forget. Never Miss. Never Ask Twice.
          </h1>
          <p style={styles.heroSubtitle}>
            Every recording expands your searchable AI knowledge base.
          </p>
          <div style={styles.highlight}>
            🎯 Recap everything! If it plays in your browser, Recapify can capture it - meetings, videos, podcasts, and more.
          </div>
          <p style={styles.heroDescription}>
            Capture meetings like Microsoft Teams and Zoom - it doesn't matter which platform, as long as you join through Chrome. We automatically transcribe your recordings, generate AI summaries, and provide an AI Assistant that answers questions across your entire knowledge base. One knowledge base for all your recordings.
          </p>
          <div style={styles.buttonContainer}>
            <button 
              style={styles.buttonPrimary}
              onClick={handleGetStarted}
              onMouseOver={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 12px rgba(0,0,0,0.15)';
              }}
              onMouseOut={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
              }}
            >
              ✨ Start for Free
            </button>
            <button 
              style={styles.buttonSecondary}
              onClick={handleWatchDemo}
              onMouseOver={(e) => {
                e.target.style.background = isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)';
                e.target.style.borderColor = isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.3)';
              }}
              onMouseOut={(e) => {
                e.target.style.background = isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)';
                e.target.style.borderColor = isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.2)';
              }}
            >
              🎬 Browse Demo
            </button>
          </div>
          <p style={{
            fontSize: '1rem',
            fontWeight: '600',
            marginBottom: '10px',
            background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}>
            🎁 Limited offer: Sign up now and get $10 in free credits.
          </p>
          <p style={{ fontSize: '0.9rem', opacity: '0.9' }}>
            Chrome extension installs in seconds • Cancel anytime • No fixed fees or subscriptions
          </p>
        </div>
      </div>

      {/* Screenshots Section */}
      {/* <div style={{ ...styles.section, maxWidth: '85%', padding: '80px 40px' }}> */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>The Flow</h2>
        <p style={{ ...styles.sectionSubtitle, marginBottom: '40px' }}>
          Simple, intuitive interface from recording to insights.
        </p>
        
        {/* Images Grid */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: isMobile ? '1fr' : '1fr 3fr', 
          gap: '60px', 
          marginTop: '10px',
          overflow: 'visible',
        }}>
          {/* Chrome Extension Column */}
          <div style={{ overflow: 'visible' }}>
            <h3 style={{ fontSize: '1.5rem', fontWeight: '700', marginBottom: '20px', textAlign: 'center', color: isDark ? '#ffffff' : '#1f2937' }}>
              Chrome Extension
            </h3>
            <div style={{ overflow: 'visible', position: 'relative' }}>
              <img 
                src={isDark ? "/extension-dark.png" : "/extension-light.png"}
                alt="Recapify Chrome Extension Interface" 
                style={{ 
                  width: '100%', 
                  height: 'auto', 
                  maxHeight: '450px',
                  objectFit: 'contain',
                  borderRadius: '12px',
                  display: 'block',
                  boxShadow: isDark ? '0 12px 35px rgba(102, 126, 234, 0.5)' : '0 12px 35px rgba(0,0,0,0.12)',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  position: 'relative',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateX(25%) scale(1.25)';
                  e.currentTarget.style.boxShadow = isDark ? '0 20px 60px rgba(102, 126, 234, 0.7)' : '0 20px 60px rgba(0,0,0,0.2)';
                  e.currentTarget.style.zIndex = '100';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateX(0) scale(1)';
                  e.currentTarget.style.boxShadow = isDark ? '0 12px 35px rgba(102, 126, 234, 0.5)' : '0 12px 35px rgba(0,0,0,0.12)';
                  e.currentTarget.style.zIndex = '1';
                }}
              />
            </div>
          </div>

          {/* Web App Column */}
          <div style={{ overflow: 'visible' }}>
            <h3 style={{ fontSize: '1.5rem', fontWeight: '700', marginBottom: '20px', textAlign: 'center', color: isDark ? '#ffffff' : '#1f2937' }}>
              Web App
            </h3>
            <div style={{ overflow: 'visible', position: 'relative' }}>
              <img 
                src={isDark ? "/web-app-dark.png" : "/web-app-light.png"}
                alt="Recapify Web App Interface" 
                style={{ 
                  width: '100%', 
                  height: 'auto', 
                  borderRadius: '12px',
                  display: 'block',
                  boxShadow: isDark ? '0 12px 35px rgba(102, 126, 234, 0.5)' : '0 12px 35px rgba(0,0,0,0.12)',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  position: 'relative',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateX(-16.67%) scale(1.25)';
                  e.currentTarget.style.boxShadow = isDark ? '0 20px 60px rgba(102, 126, 234, 0.7)' : '0 20px 60px rgba(0,0,0,0.2)';
                  e.currentTarget.style.zIndex = '100';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateX(0) scale(1)';
                  e.currentTarget.style.boxShadow = isDark ? '0 12px 35px rgba(102, 126, 234, 0.5)' : '0 12px 35px rgba(0,0,0,0.12)';
                  e.currentTarget.style.zIndex = '1';
                }}
              />
            </div>
          </div>
        </div>

        {/* Step Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '40px', marginTop: '40px' }}>
          <div style={styles.stepCard}>
            <div style={{ fontSize: '3rem', marginBottom: '20px' }}>🎬</div>
            <h3 style={styles.stepTitle}>Record with One Click</h3>
            <p style={styles.stepDescription}>
              Choose your tab and audio sources, then record. Real-time upload means instant availability. Works with any browser media.
            </p>
          </div>
          <div style={styles.stepCard}>
            <div style={{ fontSize: '3rem', marginBottom: '20px' }}>🧠</div>
            <h3 style={styles.stepTitle}>Automatic AI Processing</h3>
            <p style={styles.stepDescription}>
              Once recording completes, AI transcribes, summarizes, and indexes everything for semantic search. Fully transparent. Fully automated.
            </p>
          </div>
          <div style={styles.stepCard}>
            <div style={{ fontSize: '3rem', marginBottom: '20px' }}>🔎🤖</div>
            <h3 style={styles.stepTitle}>Search & AI Assistant</h3>
            <p style={styles.stepDescription}>
              Search by keywords or ask the AI Assistant questions in natural language. Get instant answers with citations from across all your recordings.
            </p>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div style={styles.sectionAlt}>
        <div style={styles.sectionAltInner}>
          <h2 style={styles.sectionTitle}>The Features</h2>
          <p style={styles.sectionSubtitle}>
            Powerful features designed to transform how you handle recordings.
          </p>
          <div style={styles.featuresGrid}>
            {features.map((feature, index) => (
              <div 
                key={index} 
                style={styles.featureCard}
              >
                <div style={styles.featureIcon}>{feature.icon}</div>
                <h3 style={styles.featureTitle}>{feature.title}</h3>
                <p style={styles.featureDescription}>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Security Section */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>The Security</h2>
        <p style={styles.sectionSubtitle}>
          Your data stays private and secure. Built with enterprise-grade protection.
        </p>
        <div style={styles.securityGrid}>
            <div style={styles.securityCard}>
              <div style={styles.securityIcon}>🔒</div>
              <h3 style={styles.securityTitle}>Automatic Encryption</h3>
              <p style={styles.securityDescription}>
                All recordings encrypted automatically with 256-bit encryption. Your data is protected at rest and in transit. No configuration needed.
              </p>
            </div>
            <div style={styles.securityCard}>
              <div style={styles.securityIcon}>👤</div>
              <h3 style={styles.securityTitle}>Complete Isolation</h3>
              <p style={styles.securityDescription}>
                Your recordings live in your private space. Other users can't see, access, or search your content. Complete separation between accounts.
              </p>
            </div>
            <div style={styles.securityCard}>
              <div style={styles.securityIcon}>🤖</div>
              <h3 style={styles.securityTitle}>AI Privacy Guarantee</h3>
              <p style={styles.securityDescription}>
                AI processes your transcripts but never trains on your data. Your conversations stay confidential. Never used to improve someone else's models.
              </p>
            </div>
            <div style={styles.securityCard}>
              <div style={styles.securityIcon}>🔐</div>
              <h3 style={styles.securityTitle}>Zero-Trust Access</h3>
              <p style={styles.securityDescription}>
                Authentication required for everything. No anonymous access, no shared credentials. Only you can access your recordings. Period.
              </p>
            </div>
            <div style={styles.securityCard}>
              <div style={styles.securityIcon}>🔑</div>
              <h3 style={styles.securityTitle}>You Own Your Data</h3>
              <p style={styles.securityDescription}>
                You retain full ownership of all your recordings. Delete anytime with no questions asked. Your data stays under your control. No one else can access it.
              </p>
            </div>
            <div style={styles.securityCard}>
              <div style={styles.securityIcon}>🏛️</div>
              <h3 style={styles.securityTitle}>Enterprise Infrastructure</h3>
              <p style={styles.securityDescription}>
                Built on infrastructure trusted by Fortune 500 companies. Bank-grade security without enterprise complexity. Simple and secure.
              </p>
            </div>
          </div>
      </div>

      {/* Pricing Section */}
      <div style={styles.sectionAlt}>
        <div style={styles.sectionAltInner}>
          <h2 style={styles.sectionTitle}>The Pricing</h2>
          <p style={styles.sectionSubtitle}>
            Flexible options for individuals and organizations.
          </p>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: '30px', maxWidth: '1000px', margin: '40px auto 0', padding: '0 20px' }}>
          {/* Enterprise Card */}
          <div style={{
            ...styles.pricingCard,
            border: isDark ? '3px solid rgba(102, 126, 234, 0.8)' : '3px solid rgba(59, 130, 246, 0.6)',
            boxShadow: isDark ? '0 16px 45px rgba(102, 126, 234, 0.6)' : '0 16px 45px rgba(0,0,0,0.2)',
          }}>
            <h3 style={{ ...styles.pricingTitle, textAlign: 'center' }}>Enterprise</h3>
            <p style={{ ...styles.pricingDescription, textAlign: 'center' }}>
              For organizations requiring dedicated infrastructure and full control over their data.
            </p>
            <div style={styles.price}>Custom Pricing</div>
            <div style={styles.priceUnit}>tailored to your needs</div>
            <div style={{ textAlign: 'center' }}>
              <div style={styles.featureList}>
                <div style={styles.featureItem}>✓ Deployed in your AWS account</div>
                <div style={styles.featureItem}>✓ Complete infrastructure ownership</div>
                <div style={styles.featureItem}>✓ Full data sovereignty</div>
                <div style={styles.featureItem}>✓ SSO & advanced authentication</div>
                <div style={styles.featureItem}>✓ Custom compliance requirements</div>
                <div style={styles.featureItem}>✓ Volume discounts available</div>
                <div style={styles.featureItem}>✓ Custom integrations & features</div>
              </div>
            </div>
            <button 
              style={{ 
                ...styles.buttonPrimary, 
                width: '100%',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                boxShadow: '0 6px 25px rgba(102, 126, 234, 0.6)',
              }}
              onClick={handleContactSales}
              onMouseOver={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 8px 30px rgba(102, 126, 234, 0.7)';
              }}
              onMouseOut={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 6px 25px rgba(102, 126, 234, 0.6)';
              }}
            >
              Contact Sales
            </button>
          </div>

          {/* Individual Card */}
          <div style={styles.pricingCard}>
            <h3 style={{ ...styles.pricingTitle, textAlign: 'center' }}>Individual</h3>
            <p style={{ ...styles.pricingDescription, textAlign: 'center' }}>
              Perfect for individuals and small teams. Pay only for what you use with no commitments.
            </p>
            <div style={styles.price}>Pay-as-you-go</div>
            <div style={styles.priceUnit}>only pay for what you use</div>
            <div style={{ textAlign: 'center' }}>
              <div style={styles.featureList}>
                <div style={styles.featureItem}>✓ Storage: ~$1 for 10GB/month (~200 hours audio)</div>
                <div style={styles.featureItem}>✓ Transcription: ~$1 for 5 hours audio</div>
                <div style={styles.featureItem}>✓ AI Summary: ~$2 per 1M words generated</div>
                <div style={styles.featureItem}>✓ KB Ingestion: ~$1 per 150k words added</div>
                <div style={styles.featureItem}>✓ Search: ~$1 per 2M words searched</div>
                <div style={styles.featureItem}>✓ AI Assistant: ~$1 per 250 questions answered</div>
                <div style={styles.featureItem}>✓ API requests: ~$1 per 1M requests</div>
              </div>
            </div>
            <button 
              style={{ 
                ...styles.buttonPrimary, 
                width: '100%',
                background: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                color: isDark ? '#ffffff' : '#1f2937',
                border: isDark ? '2px solid rgba(102, 126, 234, 0.4)' : '2px solid rgba(59, 130, 246, 0.3)',
              }}
              onClick={handleGetStarted}
              onMouseOver={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.background = isDark ? 'rgba(102, 126, 234, 0.2)' : 'rgba(59, 130, 246, 0.1)';
              }}
              onMouseOut={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.background = isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)';
              }}
            >
              Start Free
            </button>
          </div>
        </div>
        </div>
      </div>

      {/* CTA Section */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>
          Start Building Your Knowledge Base.
        </h2>
          <div style={styles.buttonContainer}>
            <button 
              style={styles.buttonPrimary}
              onClick={handleGetStarted}
              onMouseOver={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 12px rgba(0,0,0,0.15)';
              }}
              onMouseOut={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
              }}
            >
              🎁 Claim Your $10 Credit
            </button>
            <button 
              style={styles.buttonSecondary}
              onClick={handleWatchDemo}
              onMouseOver={(e) => {
                e.target.style.background = isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)';
                e.target.style.borderColor = isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.3)';
              }}
              onMouseOut={(e) => {
                e.target.style.background = isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)';
                e.target.style.borderColor = isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.2)';
              }}
            >
              🎬 Browse Demo
            </button>
          </div>
      </div>

      {/* Contact Modal */}
      <ContactModal 
        visible={contactModalVisible}
        onDismiss={() => setContactModalVisible(false)}
        theme={theme}
      />

      {/* Footer */}
      <Footer theme={theme} />
    </div>
  );
};

export default LandingPage;
